import React, { useEffect, useMemo, useRef, useState } from "react";
import { TopNav } from "@/components/Layout";
import { api, API_BASE, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSiteSettings, normalizeWa } from "@/lib/settings";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Search, MapPin, Star, ShieldCheck, Clock, Home, ArrowRight, Check, X, Loader2,
  Beaker, Upload, FileText, Users, Zap, Filter, CheckCircle2, Truck, IndianRupee, Copy, MessageCircle, Award,
} from "lucide-react";
import { toast } from "sonner";

const CITIES = ["Bengaluru", "Mumbai", "Delhi NCR", "Pune", "Hyderabad", "Chennai", "Kolkata"];
// Central EFGH concierge WhatsApp number — falls back if super_admin hasn't set one yet.
const EFGH_WA_FALLBACK = "919999900000";
const buildWaUrl = (number, message) =>
  `https://wa.me/${normalizeWa(number || EFGH_WA_FALLBACK)}?text=${encodeURIComponent(message)}`;
const CATEGORIES = [
  { key: "Wellness", label: "Full Body", icon: "🩺" },
  { key: "Diabetes", label: "Diabetes", icon: "🩸" },
  { key: "Thyroid", label: "Thyroid", icon: "🦋" },
  { key: "Heart", label: "Heart", icon: "❤️" },
  { key: "Women's Health", label: "Women", icon: "👩" },
  { key: "Senior Care", label: "Senior", icon: "👴" },
  { key: "Liver", label: "Liver", icon: "🫁" },
  { key: "Kidney", label: "Kidney", icon: "🫘" },
  { key: "Vitamins", label: "Vitamins", icon: "💊" },
];

export default function TestBooking() {
  const { settings, platform } = useSiteSettings();
  const efghWa = settings?.whatsapp_number || EFGH_WA_FALLBACK;
  const marketplaceEnabled = platform?.marketplace_enabled !== false;
  const [city, setCity] = useState(() => localStorage.getItem("efgh_city") || "Bengaluru");
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get("q") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [detail, setDetail] = useState(null); // { kind, item }
  const [bookOpen, setBookOpen] = useState(null); // { item, lab }
  const [rxOpen, setRxOpen] = useState(false);

  useEffect(() => localStorage.setItem("efgh_city", city), [city]);

  const runSearch = async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketplace/search", { params: { q: q || undefined, city, kind: "all" } });
      setResults(r.data.results || []);
    } catch { toast.error("Search failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { runSearch(); /* eslint-disable-next-line */ }, [city]);
  // Auto-run when arriving with a ?q= query param
  useEffect(() => { if (q) runSearch(); /* eslint-disable-next-line */ }, []);

  // Group: unique products (test/package by name) then all labs offering each
  const grouped = useMemo(() => {
    const filtered = selectedCategory ? results.filter((r) => (r.category || "").toLowerCase() === selectedCategory.toLowerCase()) : results;
    const groups = new Map();
    for (const r of filtered) {
      const key = `${r.kind}:${r.name}`;
      if (!groups.has(key)) groups.set(key, { kind: r.kind, name: r.name, category: r.category, description: r.description, parameters_count: r.parameters_count, parameters: r.parameters, sample_type: r.sample_type, fasting_required: r.fasting_required, turnaround_hours: r.turnaround_hours, offers: [] });
      groups.get(key).offers.push({ price: r.price, discounted_price: r.discounted_price, lab: r.lab, id: r.id });
    }
    // Sort within: cheapest first
    for (const g of groups.values()) g.offers.sort((a, b) => (a.discounted_price || a.price) - (b.discounted_price || b.price));
    return Array.from(groups.values());
  }, [results, selectedCategory]);

  const featured = grouped.filter((g) => g.kind === "package").slice(0, 4);

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <TopNav />

      {!marketplaceEnabled && (
        <section className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="marketplace-disabled">
          <div className="w-16 h-16 rounded-2xl bg-ink/5 mx-auto grid place-items-center">🚧</div>
          <h1 className="mt-4 font-heading font-bold text-3xl text-ink">Test booking temporarily unavailable</h1>
          <p className="mt-2 text-ink/60">Our marketplace is offline right now. Please contact your lab directly or come back later.</p>
        </section>
      )}
      {marketplaceEnabled && <></>}
      {marketplaceEnabled && (
      <>
      {/* Hero: City + Search */}
      <section className="relative bg-gradient-to-br from-efgh-orange/10 via-efgh-yellow/10 to-efgh-green/10 border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
          <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">Book Diagnostic Tests</div>
          <h1 className="mt-2 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-ink leading-tight">Compare labs. Book smart. <span className="italic font-normal" style={{ color: "#4CAF50" }}>Home collection in 60 minutes.</span></h1>
          <p className="mt-3 text-ink/70 max-w-2xl">Search 500+ tests and packages across NABL-accredited labs. Compare prices, ratings and accreditations side-by-side.</p>

          <Card className="mt-8 p-3 rounded-2xl shadow-lg" data-testid="marketplace-search-bar">
            <div className="flex gap-2 items-center flex-wrap md:flex-nowrap">
              <div className="flex items-center gap-2 pl-3 border-r border-border pr-3 shrink-0">
                <MapPin className="w-4 h-4 text-efgh-orange" />
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger className="border-0 shadow-none focus:ring-0 h-10 w-32 pl-0" data-testid="city-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex-1 relative min-w-0">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                <Input data-testid="mp-search" placeholder="Search tests or packages, e.g. HbA1c, Full Body Checkup, Vitamin D" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} className="pl-9 h-11 border-0 focus-visible:ring-0" />
              </div>
              <Button data-testid="mp-search-btn" onClick={runSearch} className="h-11 px-6 bg-ink hover:bg-ink/90 text-white">Search</Button>
            </div>
          </Card>

          <div className="mt-4 flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setRxOpen(true)} data-testid="rx-open" className="h-10 border-ink text-ink">
              <FileText className="w-4 h-4 mr-2" /> Upload Prescription
            </Button>
            <a
              data-testid="wa-concierge"
              target="_blank"
              rel="noreferrer"
              href={buildWaUrl(efghWa, `Hi EFGH! I'm in ${city}${q ? ` and looking for "${q}"` : ""}. Please help me book the right diagnostic test.`)}
              className="inline-flex items-center h-10 px-4 rounded-md border border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 text-sm font-medium transition-colors"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> Book via WhatsApp
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Categories */}
        <div className="flex gap-2 flex-wrap" data-testid="category-chips">
          <button onClick={() => setSelectedCategory(null)} className={`text-sm px-3 py-2 rounded-full transition-all ${!selectedCategory ? "bg-ink text-white" : "bg-white border border-border text-ink/70 hover:border-ink"}`}>All</button>
          {CATEGORIES.map((c) => (
            <button key={c.key} data-testid={`cat-${c.key}`} onClick={() => setSelectedCategory(c.key === selectedCategory ? null : c.key)}
              className={`text-sm px-3 py-2 rounded-full transition-all ${selectedCategory === c.key ? "bg-ink text-white" : "bg-white border border-border text-ink/70 hover:border-ink"}`}>
              <span className="mr-1.5">{c.icon}</span>{c.label}
            </button>
          ))}
        </div>

        {/* Featured packages carousel */}
        {featured.length > 0 && !q && (
          <section className="mt-8">
            <h2 className="font-heading font-semibold text-xl text-ink">Featured packages in {city}</h2>
            <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {featured.map((g) => {
                const min = g.offers[0];
                return (
                  <Card key={g.name} data-testid={`featured-${g.name}`} className="p-4 rounded-2xl card-hover cursor-pointer" onClick={() => setDetail({ kind: g.kind, item: g })}>
                    <div className="text-2xl">📋</div>
                    <div className="mt-2 font-heading font-semibold text-ink text-base leading-tight">{g.name}</div>
                    <div className="text-xs text-ink/60 mt-1">{g.parameters_count}+ parameters · {g.sample_type}</div>
                    <div className="mt-3 flex items-baseline gap-2">
                      <div className="font-heading font-bold text-ink text-xl">₹{Math.floor(min.discounted_price || min.price)}</div>
                      {min.discounted_price ? <div className="text-xs text-ink/40 line-through">₹{Math.floor(min.price)}</div> : null}
                    </div>
                    <div className="mt-1 text-[11px] text-efgh-green font-medium">from {g.offers.length} labs</div>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        {/* Results */}
        <section className="mt-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading font-semibold text-xl text-ink">{q ? `Results for "${q}"` : selectedCategory ? `${selectedCategory} tests & packages` : "All tests & packages"}</h2>
              <p className="text-sm text-ink/60">Showing {grouped.length} unique item(s) across empanelled labs in {city}</p>
            </div>
            {loading && <Loader2 className="w-5 h-5 animate-spin text-ink/40" />}
          </div>

          <div className="mt-4 space-y-3">
            {grouped.length === 0 && !loading && (
              <Card className="p-10 text-center text-ink/50 rounded-2xl">No results. Try a broader search.</Card>
            )}
            {grouped.map((g) => <ResultRow key={g.name + g.kind} g={g} city={city} onDetail={() => setDetail({ kind: g.kind, item: g })} onBook={(offer) => setBookOpen({ item: g, lab: offer.lab, price: offer.discounted_price || offer.price, offerId: offer.id })} />)}
          </div>
        </section>
      </div>

      {/* Detail modal */}
      <DetailModal detail={detail} city={city} onClose={() => setDetail(null)} onBook={(offer) => { setBookOpen({ item: detail.item, lab: offer.lab, price: offer.discounted_price || offer.price, offerId: offer.id }); setDetail(null); }} />

      {/* Booking */}
      <BookingDialog data={bookOpen} onClose={() => setBookOpen(null)} />

      {/* Prescription upload */}
      <PrescriptionDialog open={rxOpen} onClose={() => setRxOpen(false)} onExtracted={(matched) => {
        setRxOpen(false);
        if (matched.length === 0) { toast.info("No tests extracted"); return; }
        // Kick off a booking with the first available offerer of the matched tests
        toast.success(`Extracted ${matched.length} test(s) — showing pricing options`);
        setQ(matched[0].name);
        runSearch();
      }} />
      </>
      )}
    </div>
  );
}

/* ============ RESULT ROW ============ */
function ResultRow({ g, city, onDetail, onBook }) {
  const min = g.offers[0];
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="rounded-2xl overflow-hidden" data-testid={`result-${g.name}`}>
      <div className="p-5 grid md:grid-cols-[1fr_auto] gap-4 items-center">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={`pill ${g.kind === "package" ? "pill-borderline" : "pill-low"} text-[10px]`}>{g.kind.toUpperCase()}</span>
            <span className="text-xs text-ink/50">{g.category}</span>
          </div>
          <button onClick={onDetail} className="mt-1 font-heading font-semibold text-lg text-ink text-left hover:underline">{g.name}</button>
          <p className="text-sm text-ink/60 mt-1 line-clamp-2">{g.description}</p>
          <div className="mt-3 flex gap-3 items-center text-xs text-ink/60 flex-wrap">
            <span className="flex items-center gap-1"><Beaker className="w-3.5 h-3.5" /> {g.parameters_count}+ parameters</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {g.turnaround_hours}h report</span>
            {g.fasting_required && <span className="pill pill-borderline text-[10px]">Fasting</span>}
            <span>{g.sample_type}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-ink/50">Starts from</div>
          <div className="font-heading font-bold text-2xl text-ink">₹{Math.floor(min.discounted_price || min.price)}</div>
          <div className="text-xs text-efgh-green">at {min.lab?.name}</div>
          <Button onClick={() => setExpanded((v) => !v)} data-testid={`compare-${g.name}`} variant="outline" className="mt-2 h-9 border-ink text-ink">
            <Filter className="w-3.5 h-3.5 mr-1.5" /> {expanded ? "Hide" : `Compare ${g.offers.length} labs`}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border bg-muted/30 p-4 space-y-2">
          {g.offers.map((o) => <LabOfferRow key={o.id + o.lab?.id} offer={o} itemName={g.name} kind={g.kind} city={city} onBook={() => onBook(o)} />)}
        </div>
      )}
    </Card>
  );
}

function LabOfferRow({ offer, itemName, kind, city, onBook }) {
  const { lab, price, discounted_price } = offer;
  const final = Math.floor(discounted_price || price);
  const initials = (lab?.name || "L").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const waMsg = `Hi ${lab?.name || "Lab"}! I'd like to book *${itemName}* (${kind})${city ? ` in ${city}` : ""}. Listed price on EFGH: ₹${final}. Please confirm slot & home-collection details.`;
  const waHref = buildWaUrl(lab?.whatsapp_number, waMsg);
  return (
    <div className="grid md:grid-cols-[auto_1fr_auto_auto_auto] gap-3 items-center bg-white p-3 rounded-xl border border-border">
      {lab?.logo_url ? (
        <img src={lab.logo_url} alt={lab.name} className="w-11 h-11 rounded-lg object-contain bg-muted p-1" />
      ) : (
        <div className="w-11 h-11 rounded-lg bg-ink text-white grid place-items-center text-xs font-heading font-bold">{initials}</div>
      )}
      <div className="min-w-0">
        <div className="font-medium text-ink truncate">{lab?.name}</div>
        <div className="flex items-center gap-2 text-xs text-ink/60 mt-0.5 flex-wrap">
          <span className="flex items-center gap-1 text-efgh-yellow"><Star className="w-3 h-3 fill-current" /> {lab?.rating || 4.5}</span>
          <span className="text-ink/40">({lab?.rating_count || 0})</span>
          {lab?.home_collection && <span className="pill pill-normal text-[10px]"><Home className="w-2.5 h-2.5" /> Home Coll.</span>}
          {(lab?.accreditations || []).slice(0, 3).map((a) => (
            <span key={a} className="pill bg-efgh-blue/10 text-efgh-blue text-[10px]"><Award className="w-2.5 h-2.5" /> {a}</span>
          ))}
        </div>
      </div>
      <div className="text-right">
        <div className="font-heading font-bold text-lg text-ink">₹{final}</div>
        {discounted_price && <div className="text-[10px] text-ink/40 line-through">₹{Math.floor(price)}</div>}
      </div>
      <a
        data-testid={`wa-${lab?.slug}`}
        target="_blank"
        rel="noreferrer"
        href={waHref}
        title="WhatsApp lab"
        className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 transition-colors"
      >
        <MessageCircle className="w-4 h-4" />
      </a>
      <Button data-testid={`book-${lab?.slug}`} onClick={onBook} className="bg-ink hover:bg-ink/90 text-white h-10">Book <ArrowRight className="w-4 h-4 ml-1" /></Button>
    </div>
  );
}

/* ============ DETAIL MODAL ============ */
function DetailModal({ detail, city, onClose, onBook }) {
  if (!detail) return null;
  const g = detail.item;
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto" data-testid="detail-modal">
        <DialogTitle className="font-heading text-xl">{g.name}</DialogTitle>
        <div className="text-xs uppercase tracking-widest text-ink/50">{detail.kind} · {g.category}</div>
        <p className="text-sm text-ink/75 mt-2 leading-relaxed">{g.description}</p>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Parameters" value={`${g.parameters_count}+`} />
          <Stat label="Report time" value={`${g.turnaround_hours}h`} />
          <Stat label="Sample" value={g.sample_type} />
          <Stat label="Fasting" value={g.fasting_required ? "Required" : "Not needed"} />
        </div>
        {g.parameters && g.parameters.length > 0 && (
          <div className="mt-5">
            <div className="font-heading font-semibold text-ink text-sm">Parameters covered</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {g.parameters.map((p) => <span key={p} className="pill bg-muted text-ink/70 text-[11px]">{p}</span>)}
            </div>
          </div>
        )}
        <div className="mt-6 pt-4 border-t border-border">
          <div className="font-heading font-semibold text-ink">Book at your preferred lab</div>
          <div className="mt-3 space-y-2">
            {g.offers.map((o) => <LabOfferRow key={o.id + o.lab?.id} offer={o} itemName={g.name} kind={g.kind} city={city} onBook={() => onBook(o)} />)}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-3 rounded-xl bg-muted/50 text-center">
      <div className="text-xs text-ink/60">{label}</div>
      <div className="mt-1 font-heading font-semibold text-ink">{value}</div>
    </div>
  );
}

/* ============ BOOKING DIALOG ============ */
function BookingDialog({ data, onClose }) {
  const { settings } = useSiteSettings();
  const efghWa = settings?.whatsapp_number || EFGH_WA_FALLBACK;
  const { user } = useAuth();
  const [family, setFamily] = useState([]);
  const [familyMember, setFamilyMember] = useState("self");
  const [form, setForm] = useState({ patient_name: "", patient_email: "", patient_phone: "", patient_age: "", patient_gender: "male", address: "", slot_date: "", slot_time: "09:00", notes: "" });
  const [paymentMethod, setPaymentMethod] = useState("at_lab");
  const [placed, setPlaced] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!data) { setPlaced(null); return; }
    if (user?.role === "patient") {
      api.get("/family").then((r) => setFamily(r.data || [])).catch(() => {});
      setForm((f) => ({ ...f, patient_name: user.full_name || "", patient_email: user.email || "", patient_phone: user.phone || "" }));
    }
  }, [data, user]);

  useEffect(() => {
    if (familyMember === "self") {
      setForm((f) => ({ ...f, patient_name: user?.full_name || f.patient_name, patient_email: user?.email || f.patient_email, patient_phone: user?.phone || f.patient_phone, patient_age: "", patient_gender: "male" }));
    } else {
      const m = family.find((x) => x.id === familyMember);
      if (m) setForm((f) => ({ ...f, patient_name: m.full_name, patient_phone: m.phone || f.patient_phone, patient_email: m.email || f.patient_email, patient_age: m.age || "", patient_gender: m.gender || "male" }));
    }
  }, [familyMember, family, user]);

  if (!data) return null;
  const { item, lab, price, offerId } = data;

  const submit = async (e) => {
    e.preventDefault();
    // Guest booking: name + phone are mandatory before we can accept the order.
    if (!user) {
      if (!form.patient_name?.trim()) return toast.error("Please enter your full name to continue");
      if (!/^[+\d\s()-]{8,}$/.test(form.patient_phone || "")) return toast.error("Please enter a valid phone number");
    }
    setBusy(true);
    try {
      const body = {
        tests: item.kind === "test" ? [offerId] : [],
        package_ids: item.kind === "package" ? [offerId] : [],
        patient_name: form.patient_name, patient_email: form.patient_email, patient_phone: form.patient_phone,
        patient_age: form.patient_age ? Number(form.patient_age) : null,
        patient_gender: form.patient_gender,
        family_member_id: familyMember !== "self" ? familyMember : null,
        address: form.address, slot_date: form.slot_date, slot_time: form.slot_time, notes: form.notes,
        lab_id: lab?.id,
        payment_method: paymentMethod,
      };
      const r = await api.post("/bookings", body);
      setPlaced(r.data);
      toast.success("Booking confirmed!");
    } catch (err) { toast.error(apiError(err, "Booking failed")); }
    finally { setBusy(false); }
  };

  const upiUrl = (b) => `${API_BASE}/upi/qr?vpa=${encodeURIComponent(lab?.upi_id || `${lab?.slug || "lab"}@paytm`)}&name=${encodeURIComponent(lab?.name || "")}&amount=${b.total_amount}&note=${encodeURIComponent("EFGH-" + b.id.slice(0, 8))}`;
  const waUrl = (b) => `https://wa.me/${normalizeWa(lab?.whatsapp_number || efghWa)}?text=${encodeURIComponent(`Booking confirmation from EFGH\nRef: ${b.id.slice(0, 8)}\nItem: ${item.name}\nSlot: ${b.slot_date} ${b.slot_time}\nAddress: ${b.address}\nAmount: ₹${b.total_amount}`)}`;

  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-auto" data-testid="booking-dialog">
        {!placed ? (
          <>
            <DialogTitle className="font-heading">Book · {item.name}</DialogTitle>
            <div className="text-sm text-ink/60">at <b>{lab?.name}</b> — ₹{Math.floor(price)}</div>
            <form onSubmit={submit} className="mt-4 space-y-3">
              {user?.role === "patient" && (
                <div>
                  <Label>Book for</Label>
                  <Select value={familyMember} onValueChange={setFamilyMember}>
                    <SelectTrigger data-testid="family-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="self">Myself</SelectItem>
                      {family.map((f) => <SelectItem key={f.id} value={f.id}>{f.full_name} ({f.relation})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-ink/50 mt-1">Add more family members in your patient dashboard.</p>
                </div>
              )}
              {!user && (
                <div className="p-3 rounded-lg bg-efgh-yellow/10 border border-efgh-yellow/30 text-xs text-ink/80 mb-3" data-testid="guest-booking-notice">
                  Booking as a guest — please enter your <b>name and phone</b> so the lab can confirm your slot. <a href="/register" className="text-brand font-medium underline">Register</a> to save family members and track history.
                </div>
              )}
              <div><Label>Full name</Label><Input required data-testid="bk-name" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Email</Label><Input required type="email" data-testid="bk-email" value={form.patient_email} onChange={(e) => setForm({ ...form, patient_email: e.target.value })} /></div>
                <div><Label>Phone</Label><Input required data-testid="bk-phone" value={form.patient_phone} onChange={(e) => setForm({ ...form, patient_phone: e.target.value })} /></div>
              </div>
              <div><Label>Address</Label><Textarea required rows={2} data-testid="bk-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Slot date</Label><Input required type="date" data-testid="bk-date" value={form.slot_date} onChange={(e) => setForm({ ...form, slot_date: e.target.value })} /></div>
                <div>
                  <Label>Time</Label>
                  <Select value={form.slot_time} onValueChange={(v) => setForm({ ...form, slot_time: v })}>
                    <SelectTrigger data-testid="bk-time"><SelectValue /></SelectTrigger>
                    <SelectContent>{["07:00","08:00","09:00","10:00","11:00","17:00","18:00"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Payment</Label>
                <div className="mt-1 grid grid-cols-3 gap-2">
                  <button type="button" onClick={() => setPaymentMethod("upi")} data-testid="pay-upi" className={`p-3 rounded-xl border text-left text-sm ${paymentMethod === "upi" ? "border-ink bg-muted" : "border-border"}`}>
                    <div className="flex items-center gap-2"><IndianRupee className="w-4 h-4 text-efgh-green" /><b>UPI now</b></div>
                    <div className="text-[11px] text-ink/50 mt-1">Scan QR after booking</div>
                  </button>
                  <button type="button" onClick={() => setPaymentMethod("upi_after")} data-testid="pay-upi-after" className={`p-3 rounded-xl border text-left text-sm ${paymentMethod === "upi_after" ? "border-ink bg-muted" : "border-border"}`}>
                    <div className="flex items-center gap-2"><IndianRupee className="w-4 h-4 text-efgh-blue" /><b>UPI at pickup</b></div>
                    <div className="text-[11px] text-ink/50 mt-1">QR when phlebotomist arrives</div>
                  </button>
                  <button type="button" onClick={() => setPaymentMethod("at_lab")} data-testid="pay-at-lab" className={`p-3 rounded-xl border text-left text-sm ${paymentMethod === "at_lab" ? "border-ink bg-muted" : "border-border"}`}>
                    <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-efgh-orange" /><b>Cash</b></div>
                    <div className="text-[11px] text-ink/50 mt-1">Pay at collection</div>
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 bg-ink hover:bg-ink/90 text-white" data-testid="bk-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Confirm booking · ₹{Math.floor(price)}
              </Button>
            </form>
          </>
        ) : (
          <div>
            <DialogTitle className="font-heading">Booking confirmed 🎉</DialogTitle>
            <div className="mt-3 p-4 rounded-xl bg-muted text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><div className="text-[10px] uppercase tracking-widest text-ink/50">Ref</div><div className="font-mono font-medium text-ink">{placed.id.slice(0, 8).toUpperCase()}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-ink/50">Slot</div><div className="text-ink">{placed.slot_date} {placed.slot_time}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-ink/50">Amount</div><div className="font-heading font-bold text-ink">₹{placed.total_amount}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-ink/50">Payment</div><div className="text-ink capitalize">{placed.payment_method?.replace("_", " ")}</div></div>
              </div>
            </div>
            {(paymentMethod === "upi" || paymentMethod === "upi_after") && (
              <div className="mt-4 p-4 rounded-xl border border-border">
                <div className="font-heading font-semibold text-ink text-sm">Pay directly to {lab?.name}</div>
                <div className="mt-3 flex items-start gap-4">
                  <img src={upiUrl(placed)} alt="UPI QR" className="w-40 h-40 rounded-lg border border-border" data-testid="upi-qr" />
                  <div className="text-sm space-y-1">
                    <div className="text-xs text-ink/50">Scan with any UPI app</div>
                    <div className="font-mono text-ink">{lab?.upi_id || `${lab?.slug}@paytm`}</div>
                    <div className="text-xs text-ink/60">Or click <a href={`upi://pay?pa=${lab?.upi_id || `${lab?.slug}@paytm`}&pn=${encodeURIComponent(lab?.name || "")}&am=${placed.total_amount}&cu=INR&tn=EFGH-${placed.id.slice(0,8)}`} className="text-efgh-blue underline">this UPI link</a> on mobile.</div>
                  </div>
                </div>
              </div>
            )}
            <div className="mt-4 flex gap-2 flex-wrap">
              <a target="_blank" rel="noreferrer" href={waUrl(placed)} className="pill bg-[#25D366] text-white" data-testid="wa-share"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp lab</a>
              <button onClick={() => { navigator.clipboard.writeText(placed.id.slice(0,8).toUpperCase()); toast.success("Ref copied"); }} className="pill bg-muted"><Copy className="w-3.5 h-3.5" /> Copy ref</button>
            </div>
            <Button onClick={onClose} className="mt-5 w-full bg-ink hover:bg-ink/90 text-white">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ============ PRESCRIPTION UPLOAD ============ */
function PrescriptionDialog({ open, onClose, onExtracted }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!file) { toast.error("Choose a prescription"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/prescription/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch { toast.error("Could not read prescription"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="rx-dialog">
        <DialogTitle className="font-heading">Upload prescription</DialogTitle>
        <p className="text-sm text-ink/60">Snap a photo of your doctor&apos;s prescription. Our AI reads the tests and shows options across labs.</p>
        {!result ? (
          <div className="mt-4 space-y-3">
            <input ref={inputRef} type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="rx-file" />
            <button onClick={() => inputRef.current?.click()} className="w-full p-8 rounded-xl border-2 border-dashed border-border hover:border-ink transition-all text-center">
              <Upload className="w-8 h-8 mx-auto text-ink/40" />
              <div className="mt-2 text-sm text-ink font-medium">{file ? file.name : "Choose file"}</div>
              <div className="text-xs text-ink/50 mt-1">JPG, PNG or PDF up to 10 MB</div>
            </button>
            <Button onClick={submit} disabled={!file || busy} className="w-full h-11 bg-ink hover:bg-ink/90 text-white" data-testid="rx-submit">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading prescription…</> : <><Zap className="w-4 h-4 mr-2" /> Extract tests</>}
            </Button>
          </div>
        ) : (
          <div className="mt-4">
            <div className="text-sm text-ink/70">Found <b>{result.extracted_names.length}</b> tests, matched <b>{result.matched_tests.length}</b> in our catalogue.</div>
            {result.matched_tests.length > 0 && (
              <div className="mt-3 space-y-2 max-h-60 overflow-auto">
                {result.matched_tests.map((t) => (
                  <div key={t.id} className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div className="text-sm text-ink">{t.name}</div>
                    <div className="text-xs text-efgh-green">₹{t.discounted_price || t.price}</div>
                  </div>
                ))}
              </div>
            )}
            {result.unmatched_names.length > 0 && (
              <div className="mt-3 text-xs text-ink/50">Couldn&apos;t find: {result.unmatched_names.join(", ")}</div>
            )}
            <Button onClick={() => onExtracted(result.matched_tests)} className="mt-4 w-full h-11 bg-ink hover:bg-ink/90 text-white" data-testid="rx-continue">Continue</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
